import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import { observer } from "mobx-react-lite";

import { Card } from "primereact/card";
import { classNames } from "primereact/utils";

import { IconProp } from "@fortawesome/fontawesome-svg-core";

import { translationKeys } from "../../../models/app-translation-keys";
import { RouteConstants } from "../../../models/enums/route-constants.enum";
import { ActionButton } from "../../Shared/Buttons";
import { TooltipWrapper } from "../../Shared/Components/TooltipWrapper/TooltipWrapper";
import { Icon } from "../../Shared/Icon/Icon";
import { ShowTooltipWhenTextTruncated } from "../../Shared/ShowTooltipWhenTruncated/ShowTooltipWhenTruncated";

import "./FeatureCard.scss";

interface IFeatureCardProps {
  featureKey: string;
  translationKeys?: {
    title?: string;
    description?: string;
    buttonLabel?: string;
  };
  disabled?: boolean;
  featureIcon: IconProp;
  route?: RouteConstants;
  featureRoute: RouteConstants;
}

const RawFeatureCard: FunctionComponent<IFeatureCardProps> = (props: IFeatureCardProps) => {
  const { featureKey, translationKeys: featureTranslationKeys, route, featureRoute } = props;
  const { t } = useTranslation();
  const isDisabled = !!props.disabled;

  const concatRoute = route ? [route, featureRoute].join("/") : featureRoute;

  const navigate = useNavigate();

  const navigateTo = (_event: any, path: string) => {
    if (!props.disabled) {
      navigate(path.startsWith("/") ? path : `/${path}`);
    }
  };

  const cardTitle = (
    <div className="feature-card-header">
      <div className="feature-card-icon">
        <Icon icon={props.featureIcon} iconSize="2xl" />
      </div>

      <span className="feature-card-title">
        <ShowTooltipWhenTextTruncated
          tooltipContent={t(featureTranslationKeys?.title ?? `feature.${featureKey}.landing.title`)}
          tooltipPosition="top"
          lineClamp={2}
        >
          {t(featureTranslationKeys?.title ?? `feature.${featureKey}.landing.title`)}
        </ShowTooltipWhenTextTruncated>
      </span>
    </div>
  );

  return (
    <Card title={cardTitle} className="feature-card" onClick={event => !isDisabled && navigateTo(event, concatRoute)}>
      <p>{t(featureTranslationKeys?.description ?? `feature.${featureKey}.landing.description`)}</p>

      <div className="card-footer">
        <Link to={!isDisabled ? `/${concatRoute}` : "#"} className={classNames("", { disabled: isDisabled })}>
          <TooltipWrapper
            targetRef={`#${featureKey}-button`}
            position="top"
            disabled={!isDisabled}
            content={t(translationKeys.globalErrors.serviceUnavailable)}
          />

          <ActionButton
            id={`${featureKey}-button`}
            label={t(featureTranslationKeys?.buttonLabel ?? `feature.${featureKey}.landing.buttonLabel`)}
            buttonDisabled={isDisabled}
            fullWidth={true}
            centeredHorizontally={true}
            showBackground
          />
        </Link>
      </div>
    </Card>
  );
};

export const FeatureCard = observer(RawFeatureCard);
